import { Component } from '@theme/component';
import { ABMAnalytics, createAbmDirectoryStateKey, getAbmReferrerContext } from './abm-analytics.js';

const VEHICLE_LABELS = { bicycle: 'Bici', ebike: 'E-bike', scooter: 'Monopattino' };
const SERVICE_LABELS = {
  tyres: 'Foratura e pneumatici',
  brakes: 'Freni',
  drivetrain: 'Trasmissione',
  battery: 'Batteria',
  motor: 'Motore',
  diagnostics: 'Diagnostica',
  electronics: 'Elettronica',
  'general-maintenance': 'Manutenzione generale',
};
const SEARCH_KEYS = ['q', 'location', 'region', 'province', 'municipality', 'vehicle', 'service', 'page', 'per_page'];

function serializeSearchParameters(state) {
  const parameters = new URLSearchParams();
  for (const key of SEARCH_KEYS) {
    const value = String(state[key] ?? '').trim();
    if (value) parameters.set(key, value);
  }
  return parameters;
}

/** @extends {Component} */
class AbmWorkshopDirectory extends Component {
  requiredRefs = [
    'filterForm',
    'filterDetails',
    'resultCount',
    'activeFilters',
    'loadingState',
    'resultsList',
    'emptyState',
    'errorState',
    'retryButton',
    'pagination',
    'previousPage',
    'nextPage',
    'pageStatus',
    'emptyReset',
    'finalReset',
    'directoryEyebrow',
    'directoryHeading',
    'directoryIntro',
    'breadcrumbDirectory',
    'breadcrumbLocationItem',
    'breadcrumbLocation',
    'metricWorkshopCount',
    'resultsHeading',
  ];

  #events = new AbortController();
  #request;
  #state;
  #meta;
  #index;
  #indexClient;

  #genericContext = {
    eyebrow: 'Directory officine',
    heading: "Trova un'officina",
    intro: 'Cerca tra 3.948 attività indicizzate per zona, veicolo e servizio.',
    resultsHeading: 'Tutte le officine',
  };

  connectedCallback() {
    super.connectedCallback();
    this.#events.abort();
    this.#events = new AbortController();
    const { signal } = this.#events;

    this.refs.filterForm.addEventListener('submit', this.#handleSubmit, { signal });
    this.refs.filterForm.addEventListener('reset', this.#handleReset, { signal });
    this.refs.emptyReset.addEventListener('click', this.#clearFilters, { signal });
    this.refs.finalReset.addEventListener('click', this.#clearFilters, { signal });
    this.refs.retryButton.addEventListener('click', this.#retry, { signal });
    this.refs.activeFilters.addEventListener('click', this.#handleActiveFilterClick, { signal });
    this.refs.previousPage.addEventListener('click', () => this.#changePage(-1), { signal });
    this.refs.nextPage.addEventListener('click', () => this.#changePage(1), { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });

    const desktopFilters = window.matchMedia('(min-width: 1100px)');
    desktopFilters.addEventListener('change', this.#handleFilterViewportChange, { signal });
    this.#setFilterDetailsOpen(desktopFilters.matches);

    this.#state = this.#stateFromUrl();
    this.#populateForm(this.#state);
    this.#applyEditorialContext(this.#state);
    this.#load(this.#state, { trackSearch: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#events.abort();
    this.#request?.abort();
  }

  #handleSubmit = (event) => {
    event.preventDefault();
    this.#state = this.#stateFromForm();
    this.#writeUrl(this.#state, 'push');
    this.#applyEditorialContext(this.#state);
    this.#load(this.#state, { trackSearch: true });

    if (window.matchMedia('(max-width: 1099px)').matches) this.refs.filterDetails.open = false;
  };

  #handleReset = () => {
    window.requestAnimationFrame(() => {
      this.#state = this.#emptyState();
      this.#writeUrl(this.#state, 'push');
      this.#applyEditorialContext(this.#state);
      this.#load(this.#state, { trackSearch: true });
    });
  };

  #handlePopState = () => {
    this.#state = this.#stateFromUrl();
    this.#populateForm(this.#state);
    this.#applyEditorialContext(this.#state);
    this.#load(this.#state, { trackSearch: false });
  };

  #handleFilterViewportChange = (event) => this.#setFilterDetailsOpen(event.matches);

  #setFilterDetailsOpen(isDesktop) {
    if (this.refs.filterDetails instanceof HTMLDetailsElement) this.refs.filterDetails.open = isDesktop;
  }

  #clearFilters = () => {
    this.refs.filterForm.reset();
  };

  #retry = () => this.#load(this.#state, { trackSearch: false });

  #handleActiveFilterClick = (event) => {
    const button = event.target.closest('button[data-filter-key]');
    if (!(button instanceof HTMLButtonElement)) return;
    const key = button.dataset.filterKey;
    if (!['q', 'location', 'province', 'region', 'vehicle', 'service'].includes(key)) return;
    this.#state = { ...this.#state, [key]: '', page: 1 };
    if (key === 'location') this.#state.municipality = '';
    this.#populateForm(this.#state);
    this.#writeUrl(this.#state, 'push');
    this.#applyEditorialContext(this.#state);
    this.#load(this.#state, { trackSearch: true });
  };

  #changePage(direction) {
    if (!this.#meta) return;
    const nextPage = this.#meta.current_page + direction;
    if (nextPage < 1 || nextPage > this.#meta.last_page) return;

    this.#state = { ...this.#state, page: nextPage };
    this.#writeUrl(this.#state, 'push');
    this.#load(this.#state, { trackSearch: false });
    this.refs.resultsHeading.scrollIntoView({ block: 'start' });
  }

  async #load(state, { trackSearch }) {
    this.#request?.abort();
    this.#request = new AbortController();
    this.#renderActiveFilters(state);
    this.#setLoading();

    try {
      this.#indexClient ??= import(this.dataset.indexClientUrl);
      const { fetchWorkshopIndex, searchWorkshopIndex } = await this.#indexClient;

      if (!this.#index) {
        this.#index = await fetchWorkshopIndex(this.dataset.indexUrl, {
          signal: this.#request.signal,
          onMetrics: (metrics) => {
            this.dataset.indexTransferBytes = String(metrics.transfer_bytes ?? '');
            this.dataset.indexFetchMs = String(Math.round(metrics.fetch_duration_ms * 100) / 100);
            this.dataset.indexParseMs = String(Math.round((metrics.parse_duration_ms ?? 0) * 100) / 100);
          },
        });
        this.dataset.indexRecords = String(this.#index.count);
      }

      const filterStartedAt = performance.now();
      const response = searchWorkshopIndex(this.#index, state);
      this.dataset.filterMs = String(Math.round((performance.now() - filterStartedAt) * 100) / 100);
      this.#meta = response.meta;
      const renderStartedAt = performance.now();
      this.#renderResults(response.data);
      this.dataset.renderMs = String(Math.round((performance.now() - renderStartedAt) * 100) / 100);
      this.dataset.renderedCards = String(response.data.length);
      this.#renderMeta(response.meta);
      this.#trackResults(state, response.meta.total, trackSearch);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#showFailure();
    }
  }

  #setLoading() {
    this.setAttribute('aria-busy', 'true');
    this.refs.loadingState.hidden = false;
    this.refs.resultsList.hidden = true;
    this.refs.emptyState.hidden = true;
    this.refs.errorState.hidden = true;
    this.refs.pagination.hidden = true;
    this.refs.resultCount.textContent = 'Caricamento officine…';
  }

  #renderResults(workshops) {
    const fragment = document.createDocumentFragment();
    for (const workshop of workshops) fragment.append(this.#createCard(workshop));

    this.refs.resultsList.replaceChildren(fragment);
    this.refs.loadingState.hidden = true;
    this.refs.resultsList.hidden = workshops.length === 0;
    this.refs.emptyState.hidden = workshops.length !== 0;
    this.refs.errorState.hidden = true;
    this.removeAttribute('aria-busy');
  }

  #renderMeta(meta) {
    this.refs.metricWorkshopCount.textContent = new Intl.NumberFormat('it-IT').format(meta.total);
    this.refs.resultCount.textContent =
      meta.total === 1 ? '1 officina trovata' : `${new Intl.NumberFormat('it-IT').format(meta.total)} officine trovate`;
    this.refs.pageStatus.textContent = `Pagina ${meta.current_page} di ${meta.last_page}`;
    this.refs.previousPage.disabled = meta.current_page <= 1;
    this.refs.nextPage.disabled = !meta.has_more_pages;
    this.refs.pagination.hidden = meta.total === 0 || meta.last_page <= 1;
  }

  #showFailure() {
    this.#meta = null;
    this.refs.loadingState.hidden = true;
    this.refs.resultsList.hidden = true;
    this.refs.emptyState.hidden = true;
    this.refs.pagination.hidden = true;
    this.refs.errorState.hidden = false;
    this.refs.resultCount.textContent = 'Risultati temporaneamente non disponibili';
    this.removeAttribute('aria-busy');
  }

  #createCard(workshop) {
    const item = element('li', 'abm-workshop-directory__item');
    const card = element('article', 'abm-workshop-card');
    card.dataset.abmWorkshop = '';
    card.dataset.abmId = workshop.id;
    card.dataset.abmHandle = workshop.slug;
    card.dataset.abmPartnerTier = workshop.status.partner_tier;
    card.dataset.abmClaimStatus = workshop.status.claimed ? 'claimed' : 'unclaimed';
    card.dataset.abmVerificationStatus = workshop.status.verified ? 'verified' : 'unverified';

    const body = element('div', 'abm-workshop-card__body');
    const header = element('header', 'abm-workshop-card__header');
    const title = element('h3', 'abm-workshop-card__title');
    const profileUrl = storefrontUrl(workshop.profile_url);
    if (profileUrl) {
      const link = element('a', '', workshop.name);
      link.href = profileUrl;
      title.append(link);
    } else {
      title.textContent = workshop.name;
    }
    header.append(title);

    const location = [workshop.location.municipality, workshop.location.province].filter(Boolean).join(', ');
    if (location) header.append(element('p', 'abm-workshop-card__location', location));
    if (workshop.address) header.append(element('p', 'abm-workshop-card__address', workshop.address));

    const badges = element('div', 'abm-workshop-card__badges');
    if (workshop.status.partner_tier !== 'none') {
      const partnerLabel = workshop.status.partner_tier === 'partner_pro' ? 'ABM Partner Pro' : 'ABM Partner';
      badges.append(element('span', `abm-workshop-card__badge abm-workshop-card__badge--${workshop.status.partner_tier}`, partnerLabel));
    }
    if (workshop.status.verified === true) {
      badges.append(element('span', 'abm-workshop-card__badge abm-workshop-card__badge--verified', 'Profilo verificato'));
    }
    if (badges.childElementCount > 0) header.append(badges);
    body.append(header);

    const details = element('div', 'abm-workshop-card__details');
    const businessCategory = workshop.categories.business?.[0];
    if (businessCategory) details.append(element('p', 'abm-workshop-card__category', businessCategory));

    const vehicles = listFromValues(workshop.categories.vehicles, VEHICLE_LABELS, 'abm-workshop-card__tags');
    if (vehicles) details.append(vehicles);

    const serviceValues = Array.isArray(workshop.categories.services) ? workshop.categories.services : [];
    if (serviceValues.length > 0) {
      const services = element('ul', 'abm-workshop-card__services');
      services.setAttribute('aria-label', 'Servizi disponibili');
      for (const value of serviceValues.slice(0, 3)) services.append(element('li', '', SERVICE_LABELS[value] || value));
      if (serviceValues.length > 3) services.append(element('li', 'abm-workshop-card__more-services', `+${serviceValues.length - 3} altri servizi`));
      details.append(services);
    }
    body.append(details);

    body.append(
      element(
        'p',
        'abm-workshop-card__claim-status',
        workshop.status.claimed ? 'Scheda rivendicata' : 'Non rivendicata',
      ),
    );

    const contacts = this.#createContactActions(workshop);
    if (contacts) body.append(contacts);

    if (profileUrl) {
      const actions = element('div', 'abm-workshop-card__actions');
      const link = element('a', 'button', 'Vedi officina');
      link.href = profileUrl;
      actions.append(link);
      body.append(actions);
    }

    card.append(body);
    item.append(card);
    return item;
  }

  #createContactActions(workshop) {
    const actions = element('div', 'abm-workshop-card__quick-actions');
    actions.setAttribute('aria-label', `Contatta ${workshop.name}`);

    const phone = phoneHref(workshop.contacts.phone);
    if (phone) actions.append(contactLink(phone, 'Chiama', 'phone', 'directory_card'));

    const whatsapp = whatsappHref(workshop.contacts.whatsapp);
    if (whatsapp) actions.append(contactLink(whatsapp, 'WhatsApp', 'whatsapp', 'directory_card', true));

    const website = publicWebsite(workshop.contacts.website);
    if (website) actions.append(contactLink(website, 'Sito web', 'website', 'directory_card', true));

    return actions.childElementCount > 0 ? actions : null;
  }

  #stateFromUrl() {
    const params = new URL(window.location.href).searchParams;
    const state = this.#emptyState();
    for (const key of SEARCH_KEYS) {
      if (params.has(key)) state[key] = String(params.get(key) ?? '').trim();
    }
    state.page = Math.max(1, Number.parseInt(state.page, 10) || 1);
    state.per_page = Math.min(100, Math.max(1, Number.parseInt(state.per_page, 10) || 25));
    state.service = state.service === 'other' ? 'general-maintenance' : state.service;
    if (!Object.hasOwn(VEHICLE_LABELS, state.vehicle)) state.vehicle = '';
    if (!Object.hasOwn(SERVICE_LABELS, state.service)) state.service = '';
    return state;
  }

  #stateFromForm() {
    const data = new FormData(this.refs.filterForm);
    return {
      q: String(data.get('q') ?? '').trim(),
      location: String(data.get('location') ?? '').trim(),
      region: String(data.get('region') ?? '').trim(),
      province: String(data.get('province') ?? '').trim(),
      municipality: '',
      vehicle: String(data.get('vehicle') ?? ''),
      service: String(data.get('service') ?? ''),
      page: 1,
      per_page: Number.parseInt(this.dataset.perPage, 10) || 25,
    };
  }

  #emptyState() {
    return { q: '', location: '', region: '', province: '', municipality: '', vehicle: '', service: '', page: 1, per_page: 25 };
  }

  #populateForm(state) {
    setFormValue(this.refs.filterForm, 'q', state.q);
    setFormValue(this.refs.filterForm, 'location', state.location || state.municipality);
    setFormValue(this.refs.filterForm, 'province', state.province);
    setFormValue(this.refs.filterForm, 'region', state.region);
    setFormValue(this.refs.filterForm, 'vehicle', state.vehicle);
    setFormValue(this.refs.filterForm, 'service', state.service);
  }

  #renderActiveFilters(state) {
    const labels = {
      q: 'Ricerca',
      location: 'Località',
      province: 'Provincia',
      region: 'Regione',
      vehicle: 'Veicolo',
      service: 'Servizio',
    };
    const values = {
      ...state,
      location: state.location || state.municipality,
      vehicle: VEHICLE_LABELS[state.vehicle] || state.vehicle,
      service: SERVICE_LABELS[state.service] || state.service,
    };
    const fragment = document.createDocumentFragment();
    for (const key of Object.keys(labels)) {
      const value = String(values[key] ?? '').trim();
      if (!value) continue;
      const button = element('button', 'abm-workshop-directory__filter-chip', `${labels[key]}: ${value} ×`);
      button.type = 'button';
      button.dataset.filterKey = key;
      button.setAttribute('aria-label', `Rimuovi filtro ${labels[key]}: ${value}`);
      fragment.append(button);
    }
    this.refs.activeFilters.replaceChildren(fragment);
    this.refs.activeFilters.hidden = this.refs.activeFilters.childElementCount === 0;
  }

  #writeUrl(state, mode) {
    const params = serializeSearchParameters(state);
    if (params.get('page') === '1') params.delete('page');
    if (params.get('per_page') === '25') params.delete('per_page');
    const next = `${window.location.pathname}${params.size ? `?${params}` : ''}${window.location.hash}`;
    window.history[mode === 'replace' ? 'replaceState' : 'pushState'](window.history.state, '', next);
  }

  #applyEditorialContext(state) {
    const place = normalize(state.location || state.municipality);
    const isRome = place === 'roma';
    const context = isRome
      ? {
          eyebrow: 'Officine · Roma',
          heading: 'Officine per bici, e-bike\ne monopattini a Roma.',
          intro: 'Consulta officine e centri di assistenza a Roma. Filtra i risultati nazionali per veicolo, servizio e informazioni pubbliche disponibili.',
          resultsHeading: 'Officine a Roma',
        }
      : this.#genericContext;

    this.refs.directoryEyebrow.textContent = context.eyebrow;
    this.refs.directoryHeading.textContent = context.heading;
    this.refs.directoryIntro.textContent = context.intro;
    this.refs.resultsHeading.textContent = context.resultsHeading;
    this.refs.breadcrumbLocationItem.hidden = !isRome;
    if (isRome) {
      this.refs.breadcrumbDirectory.removeAttribute('aria-current');
      this.refs.breadcrumbLocation.textContent = 'Roma';
    } else {
      this.refs.breadcrumbDirectory.setAttribute('aria-current', 'page');
      this.refs.breadcrumbLocation.textContent = '';
    }
  }

  #trackResults(state, total, trackSearch) {
    const analyticsState = { ...state, partner: false };
    const stateKey = createAbmDirectoryStateKey(analyticsState, total);
    const properties = {
      q: state.q,
      vehicle: state.vehicle,
      service: state.service,
      location: state.location || state.municipality,
      result_count: total,
      source_page: trackSearch ? 'directory' : getAbmReferrerContext().source_page,
    };

    if (trackSearch) {
      ABMAnalytics.publish('abm_search_submitted', properties, {
        dedupeScope: `directory-search:${window.location.pathname}`,
        dedupeValue: stateKey,
      });

      const hasActiveFilter = [
        state.q,
        state.location,
        state.region,
        state.province,
        state.municipality,
        state.vehicle,
        state.service,
      ].some((value) => String(value ?? '').trim() !== '');
      if (hasActiveFilter) {
        ABMAnalytics.publish('abm_directory_filter_applied', properties, {
          dedupeScope: `directory-filter:${window.location.pathname}`,
          dedupeValue: stateKey,
        });
      }
    }

    const viewed = ABMAnalytics.publish('abm_directory_results_viewed', properties, {
      dedupeScope: `directory-results:${window.location.pathname}`,
      dedupeValue: stateKey,
    });
    if (viewed && total === 0) {
      ABMAnalytics.publish('abm_zero_results', properties, {
        dedupeScope: `directory-zero-results:${window.location.pathname}`,
        dedupeValue: stateKey,
      });
    }
  }
}

function element(tagName, className = '', text = '') {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function setFormValue(form, name, value) {
  const field = form.elements.namedItem(name);
  if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) field.value = value;
}

function listFromValues(values, labels, className) {
  if (!Array.isArray(values) || values.length === 0) return null;
  const list = element('ul', className);
  list.setAttribute('aria-label', 'Veicoli supportati');
  for (const value of values) list.append(element('li', '', labels[value] || value));
  return list;
}

function storefrontUrl(value) {
  try {
    const url = new URL(String(value), window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

function phoneHref(value) {
  const phone = String(value ?? '').replace(/[^+\d]/g, '').replace(/(?!^)\+/g, '');
  return phone.length >= 6 ? `tel:${phone}` : null;
}

function whatsappHref(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15 ? `https://wa.me/${digits}` : null;
}

function publicWebsite(value) {
  try {
    const url = new URL(String(value ?? ''));
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

function contactLink(href, label, type, source, external = false) {
  const link = element('a', 'abm-workshop-card__quick-action', label);
  link.href = href;
  link.dataset.abmContactType = type;
  link.dataset.abmInteractionSource = source;
  if (external) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  return link;
}

function normalize(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .trim();
}

if (!customElements.get('abm-workshop-directory')) {
  customElements.define('abm-workshop-directory', AbmWorkshopDirectory);
}
